import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
    FiMessageSquare, FiBell, FiSend, FiUsers, FiUser,
    FiCheckCircle, FiAlertCircle, FiX, FiChevronDown
} from 'react-icons/fi';
import { getUsers } from '../../lib/supabase/users';
import {
    sendNotifyTeamMessage,
    sendBulkNotifications,
    getRecipientIds
} from '../../lib/supabase/communication';

const CommunicationHub = () => {
    const { userProfile, company } = useAuth();

    // Message State
    const [messageRecipientType, setMessageRecipientType] = useState('single'); // single, role, everyone, custom
    const [messageSelectedUsers, setMessageSelectedUsers] = useState([]);
    const [messageSelectedRole, setMessageSelectedRole] = useState('');
    const [messageContent, setMessageContent] = useState('');
    const [messageSending, setMessageSending] = useState(false);
    const [messageSuccess, setMessageSuccess] = useState('');
    const [messageError, setMessageError] = useState('');

    // Notification State
    const [notificationRecipientType, setNotificationRecipientType] = useState('single');
    const [notificationSelectedUsers, setNotificationSelectedUsers] = useState([]);
    const [notificationSelectedRole, setNotificationSelectedRole] = useState('');
    const [notificationTitle, setNotificationTitle] = useState('');
    const [notificationMessage, setNotificationMessage] = useState('');
    const [notificationPriority, setNotificationPriority] = useState('normal');
    const [notificationType, setNotificationType] = useState('system_alert');
    const [notificationSending, setNotificationSending] = useState(false);
    const [notificationSuccess, setNotificationSuccess] = useState('');
    const [notificationError, setNotificationError] = useState('');

    // Users
    const [allUsers, setAllUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [showUserDropdown, setShowUserDropdown] = useState(false);
    const [showNotifUserDropdown, setShowNotifUserDropdown] = useState(false);

    useEffect(() => {
        fetchUsers();
    }, [company]);

    const fetchUsers = async () => {
        try {
            setLoadingUsers(true);
            const users = await getUsers(company.id);
            setAllUsers(users);
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoadingUsers(false);
        }
    };

    const roles = [
        { value: 'super_admin', label: 'Super Admin' },
        { value: 'admin', label: 'Admin' },
        { value: 'designer', label: 'Designer' },
        { value: 'estimator', label: 'Estimator' },
        { value: 'driver', label: 'Driver' }
    ];

    const handleSendMessage = async () => {
        try {
            setMessageSending(true);
            setMessageError('');
            setMessageSuccess('');

            // Get recipient IDs based on selection type
            const recipientIds = getRecipientIds(
                messageRecipientType,
                messageSelectedUsers,
                messageSelectedRole,
                allUsers
            );

            // Validation
            if (recipientIds.length === 0) {
                setMessageError('Please select at least one recipient');
                return;
            }

            if (!messageContent.trim()) {
                setMessageError('Please enter a message');
                return;
            }

            // Send messages from Notify Team (system user will be auto-created)
            await sendNotifyTeamMessage(
                company.id,
                recipientIds,
                messageContent
            );

            setMessageSuccess(`Message sent successfully to ${recipientIds.length} user(s)!`);
            setMessageContent('');
            setMessageSelectedUsers([]);
            setTimeout(() => setMessageSuccess(''), 3000);

        } catch (error) {
            console.error('Error sending message:', error);
            setMessageError('Failed to send message. Please try again.');
        } finally {
            setMessageSending(false);
        }
    };

    const handleSendNotification = async () => {
        try {
            setNotificationSending(true);
            setNotificationError('');
            setNotificationSuccess('');

            // Get recipient IDs based on selection type
            const recipientIds = getRecipientIds(
                notificationRecipientType,
                notificationSelectedUsers,
                notificationSelectedRole,
                allUsers
            );

            // Validation
            if (recipientIds.length === 0) {
                setNotificationError('Please select at least one recipient');
                return;
            }

            if (!notificationTitle.trim() || !notificationMessage.trim()) {
                setNotificationError('Please enter both title and message');
                return;
            }

            // Send bulk notifications
            await sendBulkNotifications(recipientIds, {
                company_id: company.id,
                title: notificationTitle,
                message: notificationMessage,
                type: notificationType,
                priority: notificationPriority
            });

            setNotificationSuccess(`Notification sent successfully to ${recipientIds.length} user(s)!`);
            setNotificationTitle('');
            setNotificationMessage('');
            setNotificationSelectedUsers([]);
            setTimeout(() => setNotificationSuccess(''), 3000);

        } catch (error) {
            console.error('Error sending notification:', error);
            setNotificationError('Failed to send notification. Please try again.');
        } finally {
            setNotificationSending(false);
        }
    };

    const toggleUserSelection = (userId, isMessage = true) => {
        if (isMessage) {
            setMessageSelectedUsers(prev =>
                prev.includes(userId)
                    ? prev.filter(id => id !== userId)
                    : [...prev, userId]
            );
        } else {
            setNotificationSelectedUsers(prev =>
                prev.includes(userId)
                    ? prev.filter(id => id !== userId)
                    : [...prev, userId]
            );
        }
    };

    const RecipientSelector = ({ type, setType, selectedUsers, selectedRole, setSelectedRole, isMessage = true }) => (
        <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
                <button
                    onClick={() => setType('single')}
                    className={`p-3 rounded-xl border-2 transition-all ${
                        type === 'single'
                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                            : 'border-gray-200 dark:border-zinc-700 hover:border-purple-300'
                    }`}
                >
                    <FiUser className={`w-5 h-5 mx-auto mb-1 ${type === 'single' ? 'text-purple-600' : 'text-gray-500'}`} />
                    <span className={`text-sm font-medium ${type === 'single' ? 'text-purple-600' : 'text-gray-600 dark:text-gray-400'}`}>
                        Single User
                    </span>
                </button>
                <button
                    onClick={() => setType('role')}
                    className={`p-3 rounded-xl border-2 transition-all ${
                        type === 'role'
                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                            : 'border-gray-200 dark:border-zinc-700 hover:border-purple-300'
                    }`}
                >
                    <FiUsers className={`w-5 h-5 mx-auto mb-1 ${type === 'role' ? 'text-purple-600' : 'text-gray-500'}`} />
                    <span className={`text-sm font-medium ${type === 'role' ? 'text-purple-600' : 'text-gray-600 dark:text-gray-400'}`}>
                        By Role
                    </span>
                </button>
                <button
                    onClick={() => setType('everyone')}
                    className={`p-3 rounded-xl border-2 transition-all ${
                        type === 'everyone'
                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                            : 'border-gray-200 dark:border-zinc-700 hover:border-purple-300'
                    }`}
                >
                    <FiCheckCircle className={`w-5 h-5 mx-auto mb-1 ${type === 'everyone' ? 'text-purple-600' : 'text-gray-500'}`} />
                    <span className={`text-sm font-medium ${type === 'everyone' ? 'text-purple-600' : 'text-gray-600 dark:text-gray-400'}`}>
                        Everyone
                    </span>
                </button>
                <button
                    onClick={() => setType('custom')}
                    className={`p-3 rounded-xl border-2 transition-all ${
                        type === 'custom'
                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                            : 'border-gray-200 dark:border-zinc-700 hover:border-purple-300'
                    }`}
                >
                    <FiUsers className={`w-5 h-5 mx-auto mb-1 ${type === 'custom' ? 'text-purple-600' : 'text-gray-500'}`} />
                    <span className={`text-sm font-medium ${type === 'custom' ? 'text-purple-600' : 'text-gray-600 dark:text-gray-400'}`}>
                        Custom
                    </span>
                </button>
            </div>

            {type === 'role' && (
                <select
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                    <option value="">Select a role</option>
                    {roles.map(role => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                </select>
            )}

            {(type === 'single' || type === 'custom') && (
                <div className="relative">
                    <button
                        onClick={() => isMessage ? setShowUserDropdown(!showUserDropdown) : setShowNotifUserDropdown(!showNotifUserDropdown)}
                        className="w-full px-4 py-3 bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-xl flex items-center justify-between"
                    >
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                            {selectedUsers.length > 0
                                ? `${selectedUsers.length} user(s) selected`
                                : 'Select users'
                            }
                        </span>
                        <FiChevronDown className="w-4 h-4 text-gray-500" />
                    </button>

                    {(isMessage ? showUserDropdown : showNotifUserDropdown) && (
                        <div className="absolute z-10 w-full mt-2 bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                            {loadingUsers ? (
                                <div className="p-4 text-center text-gray-500">Loading users...</div>
                            ) : allUsers.length === 0 ? (
                                <div className="p-4 text-center text-gray-500">No users found</div>
                            ) : (
                                allUsers.map(user => (
                                    <div
                                        key={user.id}
                                        onClick={() => toggleUserSelection(user.id, isMessage)}
                                        className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-zinc-700 cursor-pointer flex items-center justify-between border-b border-gray-100 dark:border-zinc-700 last:border-0"
                                    >
                                        <div>
                                            <p className="font-medium text-gray-900 dark:text-white text-sm">
                                                {user?.full_name || user.username}
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                                                {user.role?.replace('_', ' ')}
                                            </p>
                                        </div>
                                        {selectedUsers.includes(user.id) && (
                                            <FiCheckCircle className="w-5 h-5 text-purple-600" />
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    return (
        <div className="mobile-management-page communication-page space-y-6">
            {/* Page Header */}
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-8 text-white">
                <h1 className="text-3xl font-bold mb-2">Communication Hub</h1>
                <p className="text-purple-100">Send messages and notifications to your team from Notify Team</p>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* Message Panel */}
                <div className="bg-white dark:bg-zinc-800 rounded-xl border border-gray-200 dark:border-zinc-700 p-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
                            <FiMessageSquare className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Send Message</h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400">From Notify Team</p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        {/* Recipient Selection */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Recipients
                            </label>
                            <RecipientSelector
                                type={messageRecipientType}
                                setType={setMessageRecipientType}
                                selectedUsers={messageSelectedUsers}
                                selectedRole={messageSelectedRole}
                                setSelectedRole={setMessageSelectedRole}
                                isMessage={true}
                            />
                        </div>

                        {/* Message Content */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Message
                            </label>
                            <textarea
                                value={messageContent}
                                onChange={(e) => setMessageContent(e.target.value)}
                                rows={6}
                                placeholder="Type your message here..."
                                className="w-full px-4 py-3 bg-gray-50 dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                            />
                        </div>

                        {/* Success/Error Messages */}
                        {messageSuccess && (
                            <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
                                <FiCheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                                <span className="text-sm text-green-700 dark:text-green-300">{messageSuccess}</span>
                            </div>
                        )}

                        {messageError && (
                            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                                <FiAlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                                <span className="text-sm text-red-700 dark:text-red-300">{messageError}</span>
                            </div>
                        )}

                        {/* Send Button */}
                        <button
                            onClick={handleSendMessage}
                            disabled={messageSending}
                            className="w-full py-3 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {messageSending ? (
                                <>
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Sending...
                                </>
                            ) : (
                                <>
                                    <FiSend className="w-5 h-5" />
                                    Send Message
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Notification Panel */}
                <div className="bg-white dark:bg-zinc-800 rounded-xl border border-gray-200 dark:border-zinc-700 p-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                            <FiBell className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Send Notification</h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400">From Notify Team</p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        {/* Recipient Selection */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Recipients
                            </label>
                            <RecipientSelector
                                type={notificationRecipientType}
                                setType={setNotificationRecipientType}
                                selectedUsers={notificationSelectedUsers}
                                selectedRole={notificationSelectedRole}
                                setSelectedRole={setNotificationSelectedRole}
                                isMessage={false}
                            />
                        </div>

                        {/* Priority */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Priority
                            </label>
                            <select
                                value={notificationPriority}
                                onChange={(e) => setNotificationPriority(e.target.value)}
                                className="w-full px-4 py-3 bg-gray-50 dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="low">Low</option>
                                <option value="normal">Normal</option>
                                <option value="high">High</option>
                                <option value="urgent">Urgent</option>
                            </select>
                        </div>

                        {/* Title */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Title
                            </label>
                            <input
                                type="text"
                                value={notificationTitle}
                                onChange={(e) => setNotificationTitle(e.target.value)}
                                placeholder="Notification title..."
                                className="w-full px-4 py-3 bg-gray-50 dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        {/* Message */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Message
                            </label>
                            <textarea
                                value={notificationMessage}
                                onChange={(e) => setNotificationMessage(e.target.value)}
                                rows={4}
                                placeholder="Type your notification message here..."
                                className="w-full px-4 py-3 bg-gray-50 dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                            />
                        </div>

                        {/* Success/Error Messages */}
                        {notificationSuccess && (
                            <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
                                <FiCheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                                <span className="text-sm text-green-700 dark:text-green-300">{notificationSuccess}</span>
                            </div>
                        )}

                        {notificationError && (
                            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                                <FiAlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                                <span className="text-sm text-red-700 dark:text-red-300">{notificationError}</span>
                            </div>
                        )}

                        {/* Send Button */}
                        <button
                            onClick={handleSendNotification}
                            disabled={notificationSending}
                            className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {notificationSending ? (
                                <>
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Sending...
                                </>
                            ) : (
                                <>
                                    <FiSend className="w-5 h-5" />
                                    Send Notification
                                </>
                            )}
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default CommunicationHub;
